import React, { useState, useEffect, useRef } from 'react';
import { TableRow as MuiTableRow, TableCell, Collapse } from '@mui/material';
import { ReferralData, DeduplicationMatch } from '../types';
import ExpandedRowContent from './ExpandedRowContent';
import { renderCellValue, ColumnConfig } from './cellRenderers';
import { ExpandedFieldConfig } from '../Table';
import { STATUS, FLAG_LEAD_STATUS, MAX_OPERATION_RETRIES } from '../../LeadManagement/constants';
import { sendToCC, flagPatient, removeLeadLock } from '../../../services/leadManagementApi';
import { buildNewRecordPayload } from '../utils/buildSendToCCPayload';
import { useSelector } from 'react-redux';
import { ccPatientApi } from '../../../features/lma/ccPatientApi';
import {
  useGetCCPatientsByIdsQuery,
  useLazyGetTrackingParametersByEnrollmentIdQuery,
} from '../../../features/lma/ccPatientApi';
import {
  LeadManagementFields,
  PatientProfileTabFields,
} from '../../LeadManagement/LeadManagement.types';
import ConfirmationDialog from './ConfirmationDialog';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

interface FilterOption {
  value: string;
  rowBgColor?: string;
  label?: string;
}

interface FilterCheckboxGroup {
  options?: FilterOption[];
  label?: string;
}

interface FilterGroup {
  checkboxGroups?: FilterCheckboxGroup[];
  label?: string;
}

interface FilterConfig {
  filterGroups?: FilterGroup[];
}

interface TableRowProps {
  row: ReferralData;
  columns: ColumnConfig[];
  onSelectRow: (id: string) => void;
  onPatientCCIdChange?: (id: string, newValue: string) => void;
  onStatusChange?: (id: string, newValue: string) => void;
  currentUser: string;
  lockedRecordsCount: number;
  onLockToggle?: (id: string) => void;
  lockLoadingLeadId?: string | null;
  enableExpandableRows: boolean;
  onOpenMergeModal?: (
    match: DeduplicationMatch | undefined,
    row: ReferralData,
    setters?: {
      setIsSending?: (value: boolean) => void;
      setShowDialog?: (value: boolean) => void;
      setDialogMode?: (value: 'confirm' | 'result') => void;
      setResultMessage?: (value: string) => void;
      setResultType?: (value: 'success' | 'error') => void;
      onRemoveRecord?: (patientId: string) => void;
    }
  ) => void;
  hideDedupeSelectColumn?: boolean;
  hideSendToCCButton?: boolean;
  isProcessedTab?: boolean;
  expandedConfig?: ExpandedFieldConfig;
  isExpanded?: boolean; // Controlled expansion state from parent
  onToggleExpand?: () => void; // Handler to toggle expansion
  onRemoveRecord?: (patientId: string) => void;
  confirmationModalMessages?: LeadManagementFields;
  /** Same floating red alert as lock errors (e.g. Send to CC without a valid lock). */
  onSendToCCLockValidationError?: (message: string) => void;
  isLockingEnabled?: boolean;
  currentTabKey?: string;
  filterConfig?: FilterConfig;
  PatientProfileConfig?: PatientProfileTabFields;
}

const TableRow: React.FC<TableRowProps> = ({
  row,
  columns,
  onSelectRow,
  onPatientCCIdChange,
  onStatusChange,
  currentUser,
  lockedRecordsCount,
  onLockToggle,
  lockLoadingLeadId = null,
  enableExpandableRows,
  onOpenMergeModal,
  hideDedupeSelectColumn = false,
  hideSendToCCButton = false,
  isProcessedTab = false,
  expandedConfig,
  isExpanded = false, // Receive controlled expansion state
  onToggleExpand, // Receive toggle handler
  onRemoveRecord,
  confirmationModalMessages,
  onSendToCCLockValidationError,
  isLockingEnabled = false,
  currentTabKey,
  filterConfig,
  PatientProfileConfig,
}) => {
  const { sitecoreContext } = useSitecoreContext();
  const dateTimeFormat: string =
    (sitecoreContext as any)?.siteSettings?.DateTimeFormat?.value || 'MM/DD/YYYY HH:MM:SS';
  const dateFormat: string =
    (sitecoreContext as any)?.siteSettings?.DateFormat?.value || 'MM/DD/YYYY';

  // Use controlled state if provided, otherwise use internal state (for backwards compatibility)
  const [internalRowOpen, setInternalRowOpen] = useState(false);
  // Use isExpanded when onToggleExpand is provided (controlled mode)
  const rowOpen = onToggleExpand ? isExpanded : internalRowOpen;
  const [expandedSection, setExpandedSection] = useState<
    'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null
  >(row.deduplication && row.deduplication.length > 0 ? 'deduplication' : null);
  const [editedCCId, setEditedCCId] = useState(row.patientCCId);
  const [statusValue, setStatusValue] = useState(row.dedupeStatus || row.status || '');
  const [ccIdError, setCcIdError] = useState<string>('');
  const [newComment, setNewComment] = useState<string>('');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'confirm' | 'result' | 'retry'>('result');
  const [resultMessage, setResultMessage] = useState('');
  const [resultType, setResultType] = useState<'success' | 'error'>('success');
  const [isSending, setIsSending] = useState(false);
  const sendToCCRetryCountRef = useRef(0);

  // Get CC Patient IDs from deduplication matches
  const ccPatientIds = (row.deduplication || [])
    .map((d) => d.ccPatientReferenceId)
    .filter((id): id is string => !!id);

  // Access cached CC Patient data from Redux store using RTK Query selector
  const cachedCCPatientsData = useSelector((state: any) => {
    if (ccPatientIds.length === 0) return {};
    const selectResult = ccPatientApi.endpoints.getCCPatientsByIds.select(ccPatientIds)(state);
    return selectResult?.data || {};
  });

  const isReturningUser = statusValue === STATUS.RETURNING_USER;
  const returningUserCCIds = row.patientCCId ? [row.patientCCId] : [];
  const { data: returningUserCCData } = useGetCCPatientsByIdsQuery(returningUserCCIds, {
    skip: !isReturningUser || returningUserCCIds.length === 0 || !rowOpen,
  });

  const [fetchTrackingParamsByEnrollmentId] = useLazyGetTrackingParametersByEnrollmentIdQuery();

  // Get valid dedupe CC Ref IDs from the deduplication array or dedupeCCRefIds field
  const validDedupeCCRefIds =
    row.deduplication?.map((d) => d.ccPatientReferenceId) || row.dedupeCCRefIds || [];

  // Auto-select the first deduplication record if status is Auto-Merge and no record is selected
  const getInitialSelectedDedupeRecord = () => {
    if (statusValue === STATUS.AUTO_MERGE && validDedupeCCRefIds.length > 0) {
      return validDedupeCCRefIds[0];
    }
    return '';
  };

  const [selectedDedupeRecord, setSelectedDedupeRecord] = useState<string>(
    getInitialSelectedDedupeRecord()
  );

  // Sync state with row prop changes when row data changes (e.g., after filtering)
  useEffect(() => {
    setEditedCCId(row.patientCCId);
    setStatusValue(row.dedupeStatus || row.status || '');
  }, [row.id]);

  useEffect(() => {
    setEditedCCId(row.patientCCId);
  }, [row.patientCCId]);

  // Auto-select the first deduplication record when status changes to Auto-Merge
  useEffect(() => {
    if (
      statusValue === STATUS.AUTO_MERGE &&
      !selectedDedupeRecord &&
      validDedupeCCRefIds.length > 0
    ) {
      setSelectedDedupeRecord(validDedupeCCRefIds[0]);
    }
  }, [statusValue, selectedDedupeRecord, validDedupeCCRefIds]);

  // Validate Patient CC ID
  const validateCCId = (ccId: string, status: string): string => {
    // If status is New-Record, CC ID should be blank
    if (status === STATUS.NEW_RECORD && ccId.trim() !== '') {
      return 'CC Patient ID must be blank for New Record status';
    }

    // If status is Auto-Merge, CC ID must not be blank
    if (status === STATUS.AUTO_MERGE && ccId.trim() === '') {
      return 'CC Patient ID is required for Auto Merge status';
    }

    // Validate CC ID against valid dedupe CC Ref IDs if entered
    if (ccId.trim() !== '' && validDedupeCCRefIds.length > 0) {
      if (!validDedupeCCRefIds.includes(ccId.trim())) {
        return `Invalid CC ID. Must be one of: ${validDedupeCCRefIds.join(', ')}`;
      }
    }

    return '';
  };

  // Check if checkbox should be disabled
  // const isCheckboxDisabled = (): boolean => {
  //   // Disable if locked by another user
  //   if (row.lockedBy && row.lockedBy !== currentUser) {
  //     return true;
  //   }

  //   // Disable if status is manual review
  //   if (statusValue === STATUS.MANUAL_REVIEW_REQUIRED) {
  //     return true;
  //   }

  //   // Disable if status is Auto-Merge and CC ID is blank or invalid
  //   if (statusValue === STATUS.AUTO_MERGE) {
  //     const error = validateCCId(editedCCId, statusValue);
  //     if (error) {
  //       return true;
  //     }
  //   }

  //   return false;
  // };

  const getLeadTypeColorFromFilterConfig = (leadType: string): string | undefined => {
    if (!filterConfig) return undefined;
    const filterGroups: FilterGroup[] = filterConfig.filterGroups || [];
    const normalised = leadType.replace(/Lead$/i, '').toLowerCase();
    for (const group of filterGroups) {
      for (const checkboxGroup of group?.checkboxGroups || []) {
        for (const option of checkboxGroup?.options || []) {
          if (option?.value?.toLowerCase() === normalised && option?.rowBgColor) {
            return option.rowBgColor;
          }
        }
      }
    }
    return undefined;
  };

  const getRowBgColor = () => {
    // If locked by another user, grey out the row
    if (row.lockedBy && row.lockedBy !== currentUser) {
      return '#E8E8E8'; // Grey for locked-by-other records
    }

    // Use leadType to determine row background color
    if (row.leadType) {
      const dynamicColor = getLeadTypeColorFromFilterConfig(row.leadType);
      if (dynamicColor) return dynamicColor;

      switch (row.leadType) {
        case 'HotLead':
          return '#FFE5E5'; // Amber
        case 'WarmLead':
          return '#FFF4E5'; // Yellow
        case 'ColdLead':
          return '#E5F2FF'; // Blue
        default:
          return 'transparent';
      }
    }

    return 'transparent';
  };

  const rowBgColor = getRowBgColor();

  const handleCloseDialog = () => {
    if (dialogMode === 'result' && resultType === 'success' && onRemoveRecord) {
      onRemoveRecord(row.patientId);
    }
    setShowDialog(false);
  };

  const handleConfirmAction = async () => {
    if (dialogMode === 'confirm') {
      setShowDialog(false);
      await performSendToCC();
    } else if (dialogMode === 'retry') {
      setShowDialog(false);
      sendToCCRetryCountRef.current += 1;
      await performSendToCC();
    } else {
      handleCloseDialog();
    }
  };

  const handleCancelAction = () => {
    setShowDialog(false);
  };

  const performSendToCC = async () => {
    setIsSending(true);

    try {
      const currentStatus = statusValue || row.status;

      if (currentStatus === STATUS.AUTO_MERGE) {
        if (selectedDedupeRecord) {
          const selectedMatch = row.deduplication?.find(
            (match) => match.ccPatientReferenceId === selectedDedupeRecord
          );

          if (selectedMatch) {
            const enrichedData = cachedCCPatientsData[selectedMatch.ccPatientReferenceId];

            if (onOpenMergeModal) {
              onOpenMergeModal(enrichedData, row, {
                setIsSending,
                setShowDialog,
                setDialogMode,
                setResultMessage,
                setResultType,
                onRemoveRecord,
              });
            }
          }
        }
      } else if (
        confirmationModalMessages?.EnableComparisonForNewRecords?.value &&
        currentStatus === STATUS.NEW_RECORD
      ) {
        if (onOpenMergeModal) {
          onOpenMergeModal(undefined, row, {
            setIsSending,
            setShowDialog,
            setDialogMode,
            setResultMessage,
            setResultType,
            onRemoveRecord,
          });
        }
      } else if (currentStatus === STATUS.RETURNING_USER) {
        const enrichedData = returningUserCCData?.[row.patientCCId];
        if (onOpenMergeModal && enrichedData) {
          onOpenMergeModal(enrichedData, row, {
            setIsSending,
            setShowDialog,
            setDialogMode,
            setResultMessage,
            setResultType,
            onRemoveRecord,
          });
        }
      } else if (currentStatus === STATUS.NEW_RECORD) {
        const trackingParamsData = row.enrollmentId
          ? await fetchTrackingParamsByEnrollmentId(row.enrollmentId, true)
              .unwrap()
              .catch(() => undefined)
          : undefined;
        const payload = buildNewRecordPayload(row, statusValue, trackingParamsData);
        const response = await sendToCC(payload, { isLockingEnabled, userName: currentUser });

        if (response.success) {
          setIsSending(false);
          sendToCCRetryCountRef.current = 0;
          //auto-unlock after sending to cc
          if (isLockingEnabled && row.lockedBy === currentUser) {
            const leadId = String(row.leadId ?? row.id);
            removeLeadLock(leadId, currentUser).catch((err) =>
              console.warn('[TableRow] Auto-unlock after Send to CC failed:', err?.message)
            );
          }

          const resources = response.resources || {};
          const resourceTypes = [
            { key: 'conditions', label: 'Conditions' },
            { key: 'procedures', label: 'Procedures' },
            { key: 'medications', label: 'Medications' },
            { key: 'immunizations', label: 'Immunizations' },
            { key: 'allergies', label: 'Allergies' },
            { key: 'familyMemberHistory', label: 'Family Member History' },
            { key: 'devices', label: 'Devices' },
          ];

          const failedResources: string[] = [];
          const errorMessages: string[] = [];
          let hasSkippedResources = false;

          resourceTypes.forEach(({ key, label }) => {
            const resourceData = resources[key];
            if (resourceData && resourceData !== null) {
              if (resourceData.ccError) {
                failedResources.push(label);
                errorMessages.push(resourceData.ccError);
              }
              if (resourceData.hasSkippedResources === true) {
                hasSkippedResources = true;
              }
            }
          });

          const hasResourceError = failedResources.length > 0;

          // Build skipped resources message
          const skippedResourceMessage = hasSkippedResources
            ? 'Some Resources were skipped, check the full CDP profile in processed tab for more details.'
            : '';

          if (hasResourceError) {
            setResultType('success');
            const partialSuccessMessage =
              confirmationModalMessages?.ConfirmationModalPartialSuccessMessage?.value || '-';

            const failedResourcesList = failedResources.join(', ');
            const combinedErrors = errorMessages.join(', ');

            let formattedMessage = partialSuccessMessage
              .replace('[resources]', failedResourcesList)
              .replace('[ccError]', combinedErrors)
              .replace('[skippedResources]', skippedResourceMessage)
              .replace('[ccId]', response?.ccId);

            // Remove empty lines caused by empty placeholder replacement
            formattedMessage = formattedMessage.replace(/\n\s*\n/g, '\n').trim();

            setResultMessage(formattedMessage);
          } else {
            setResultType('success');
            let successMessage =
              confirmationModalMessages?.ConfirmationModalSuccessMessage?.value ||
              'Record sent to CC successfully';
            successMessage = successMessage
              .replace('[ccId]', response?.ccId)
              .replace('[skippedResources]', skippedResourceMessage);

            // Remove empty lines caused by empty placeholder replacement
            successMessage = successMessage.replace(/\n\s*\n/g, '\n').trim();

            setResultMessage(successMessage);
          }
          setDialogMode('result');
          setShowDialog(true);
        } else {
          setIsSending(false);
          const backendMsg =
            (response && typeof response === 'object' && 'message' in response
              ? (response as { message?: string }).message
              : undefined) || '';
          if (sendToCCRetryCountRef.current < MAX_OPERATION_RETRIES) {
            setResultMessage(
              backendMsg ||
                'The server did not confirm success (empty response). Would you like to try again?'
            );
            setDialogMode('retry');
            setShowDialog(true);
          } else {
            setResultType('error');
            setResultMessage(
              confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
                backendMsg ||
                'Failed to send record to CC'
            );
            setDialogMode('result');
            setShowDialog(true);
            sendToCCRetryCountRef.current = 0;
          }
        }
      } else {
        setIsSending(false);
        setResultType('error');
        setResultMessage(
          confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
            'Send to CC is only available for new or auto-merge status'
        );
        setDialogMode('result');
        setShowDialog(true);
      }
    } catch (error: any) {
      setIsSending(false);
      const msg = typeof error?.message === 'string' ? error.message : '';
      const isClientValidation = msg && !error?.response && error instanceof Error;
      // Only delegate lock-style errors when parent handles them (same pattern as useMergeModal).
      // Plain `Error` from sendToCC (e.g. network) must not skip the retry dialog.
      if (isClientValidation && onSendToCCLockValidationError) {
        sendToCCRetryCountRef.current = 0;
        onSendToCCLockValidationError(msg);
        return;
      }
      if (sendToCCRetryCountRef.current < MAX_OPERATION_RETRIES) {
        setResultMessage('The operation failed. Would you like to try again?');
        setDialogMode('retry');
        setShowDialog(true);
      } else {
        setResultType('error');
        setResultMessage(
          confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
            error?.response?.data?.message ||
            msg ||
            'Unknown error occurred'
        );
        setDialogMode('result');
        setShowDialog(true);
        sendToCCRetryCountRef.current = 0;
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSendToCCClick = async () => {
    const currentStatus = statusValue || row.status;

    if (
      currentStatus === STATUS.NEW_RECORD &&
      !confirmationModalMessages?.EnableComparisonForNewRecords?.value
    ) {
      setDialogMode('confirm');
      setShowDialog(true);
    } else {
      await performSendToCC();
    }
  };

  /**
   * Handle flag operation — marks the record as Flagged via the API
   */
  const handleFlagPatient = async (): Promise<void> => {
    try {
      const userId = parseInt(localStorage.getItem('lma-userId') || '0', 10);

      const success = await flagPatient({
        leadId: row.leadId || 0,
        userId,
        leadStatus: !row.isFlaggedRecord ? FLAG_LEAD_STATUS.FLAGGED : FLAG_LEAD_STATUS.UNFLAGGED,
        ccPatientId: row.patientCCId,
      });

      if (success) {
        // Auto-unlock: if the record was locked (by anyone), release the lock on flag
        if (row.lockedBy) {
          const leadId = String(row.leadId ?? row.id);
          try {
            await removeLeadLock(leadId, row.lockedBy);
          } catch (unlockErr: any) {
            console.warn('[TableRow] Auto-unlock on flag failed:', unlockErr?.message);
          }
        }

        if (onRemoveRecord) {
          onRemoveRecord(row.patientId);
        }
      } else {
        throw new Error('Flag operation failed');
      }
    } catch (error: any) {
      console.error('Flag operation failed:', error);
      throw error;
    }
  };

  return (
    <React.Fragment>
      <MuiTableRow
        title={
          row.lockedBy && row.lockedBy !== currentUser
            ? `Locked by ${row.lockedBy}. Lead ID: ${row.leadId ?? row.id}`
            : undefined
        }
        sx={{
          '& > *': { borderBottom: 'unset' },
          cursor: enableExpandableRows ? 'pointer' : 'default',
          // Static visual distinction for locked rows: border around entire row
          ...(row.lockedBy === currentUser && {
            borderLeft: '3px solid #f44336',
          }),
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
          '&:hover td': {
            backgroundColor: `${rowBgColor} !important`,
          },
        }}
        onClick={() => {
          if (enableExpandableRows) {
            // Use controlled toggle if provided, otherwise use internal state
            if (onToggleExpand) {
              onToggleExpand();
            } else {
              setInternalRowOpen(!internalRowOpen);
            }
          }
        }}
      >
        {columns.map((column) => {
          return (
            <TableCell
              key={column.id}
              align={column.align || 'left'}
              padding={column.id === 'select' ? 'checkbox' : 'normal'}
              sx={{
                width: column.width,
                maxWidth: column.maxWidth || 'none',
                minWidth: 200,
                backgroundColor: rowBgColor,
                wordWrap: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              {renderCellValue({
                column,
                row,
                rowOpen,
                setRowOpen: onToggleExpand || (() => setInternalRowOpen(!internalRowOpen)),
                enableExpandableRows,
                onSelectRow,
                isCheckboxDisabled: true,
                editedCCId,
                setEditedCCId,
                statusValue,
                setStatusValue,
                ccIdError,
                setCcIdError,
                validateCCId,
                onPatientCCIdChange,
                onStatusChange,
                currentUser,
                lockedRecordsCount,
                onLockToggle,
                lockLoadingLeadId,
                onOpenMergeModal,
                hideSendToCCButton,
                dateTimeFormat,
                dateFormat,
              })}
            </TableCell>
          );
        })}
      </MuiTableRow>
      {enableExpandableRows && (
        <MuiTableRow sx={{ backgroundColor: '#f8f9fa' }}>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length}>
            <Collapse in={rowOpen} timeout="auto" unmountOnExit>
              <ExpandedRowContent
                row={row}
                expandedSection={expandedSection}
                setExpandedSection={setExpandedSection}
                newComment={newComment}
                setNewComment={setNewComment}
                onOpenMergeModal={onOpenMergeModal}
                onSendToCC={hideSendToCCButton ? undefined : handleSendToCCClick}
                isSending={isSending}
                selectedDedupeRecord={selectedDedupeRecord}
                setSelectedDedupeRecord={setSelectedDedupeRecord}
                hideDedupeSelectColumn={hideDedupeSelectColumn}
                isProcessedTab={isProcessedTab}
                expandedConfig={expandedConfig}
                statusValue={statusValue}
                onFlagPatient={handleFlagPatient}
                currentUser={currentUser}
                lockedRecordsCount={lockedRecordsCount}
                isLockingEnabled={isLockingEnabled}
                currentTabKey={currentTabKey}
                PatientProfileConfig={PatientProfileConfig}
              />
            </Collapse>
          </TableCell>
        </MuiTableRow>
      )}

      <ConfirmationDialog
        open={showDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        mode={dialogMode}
        message={
          dialogMode === 'confirm'
            ? confirmationModalMessages?.ConfirmText?.value ||
              'Are you sure you want to send this record to CC?'
            : resultMessage
        }
        title="Confirm Send to CC"
        confirmButtonText={
          dialogMode === 'confirm'
            ? confirmationModalMessages?.ConfirmButtonText?.value || 'Yes'
            : dialogMode === 'retry'
            ? 'Retry'
            : 'Done'
        }
        cancelButtonText={confirmationModalMessages?.ConfirmDismissButtonText?.value || 'Cancel'}
      />
    </React.Fragment>
  );
};

export default TableRow;
